import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useAgentStream } from "@/hooks/useAgentStream";
import { MessageList } from "./MessageList";
import { ClarificationPanel } from "./ClarificationPanel";
import { LimitReachedCard } from "./LimitReachedCard";
import { QuotaIndicator } from "./QuotaIndicator";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Send, Loader2, Sparkles, RotateCcw } from "lucide-react";
import { cn } from "@/utils/cn";
import { clearChat } from "@/store/slices/aiChatSlice";

export const AiChatPanel: React.FC = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { sendMessage, resumeConversation, abortStream, isWaitingForReply } =
    useAgentStream();
  const isStreaming = useAppSelector((state) => state.aiChat.isStreaming);
  const error = useAppSelector((state) => state.aiChat.error);
  const limitReached = useAppSelector((state) => state.aiChat.limitReached);
  const questions = useAppSelector((state) => state.aiChat.questions);
  const sessionId = useAppSelector((state) => state.aiChat.sessionId);

  // A resumable limit (turn cap) is a pause, not a dead end — the session and
  // its context are still there, so the user can keep typing. Only limits with
  // no way forward (budget, quota, expired session) lock the composer.
  const isBlockingLimit = Boolean(limitReached) && !limitReached?.resumable;
  const inputDisabled = (isStreaming && !isWaitingForReply) || isBlockingLimit;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || inputDisabled) return;
    sendMessage(input.trim());
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  const handleReset = () => {
    abortStream();
    dispatch(clearChat());
    setInput("");
    setResetDialogOpen(false);
  };

  return (
    <>
      <div className="flex flex-col bg-background h-full rounded-xl border shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-b bg-muted/30">
          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 shrink-0">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-none">
              {t("aiAgent.panel.title")}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isStreaming && !isWaitingForReply
                ? t("aiAgent.panel.statusGenerating")
                : isWaitingForReply
                  ? t("aiAgent.panel.statusWaiting")
                  : t("aiAgent.panel.statusReady")}
            </p>
          </div>
          {/* Quota inline — always visible in header, right side */}
          <QuotaIndicator variant="inline" />
          {isStreaming && !isWaitingForReply && (
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={() => setResetDialogOpen(true)}
            aria-label={t("aiAgent.panel.resetLabel")}
            title={t("aiAgent.panel.resetLabel")}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Message list */}
        <MessageList />

        {/* Limit reached card */}
        {limitReached && (
          <LimitReachedCard
            reason={limitReached.reason}
            numTurns={limitReached.numTurns}
            percentUsed={limitReached.percentUsed}
            resetAt={limitReached.resetAt}
            resumable={limitReached.resumable}
            resumesLeft={limitReached.resumesLeft}
            onContinue={resumeConversation}
            onNewConversation={() => dispatch(clearChat())}
          />
        )}

        {/* Error banner */}
        {error && !limitReached && (
          <div className="mx-4 mb-2 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-sm border border-destructive/20">
            {error}
          </div>
        )}

        {/* Input area */}
        {questions ? (
          <ClarificationPanel
            questions={questions}
            sessionId={sessionId ?? ""}
          />
        ) : (
          <div className="border-t bg-background/95 backdrop-blur-sm">
            {isWaitingForReply && (
              <div className="flex items-center gap-1.5 px-4 pt-2.5 text-xs text-primary font-medium">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                {t("aiAgent.panel.replyHint")}
              </div>
            )}
            <form onSubmit={handleSubmit} className="p-3 flex gap-2 items-end">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  isWaitingForReply
                    ? t("aiAgent.panel.placeholderReply")
                    : t("aiAgent.panel.placeholderDefault")
                }
                className={cn(
                  "resize-none min-h-[44px] max-h-32 text-sm transition-shadow duration-200",
                  isWaitingForReply &&
                    "ring-2 ring-primary/40 border-primary/40",
                )}
                rows={2}
                disabled={inputDisabled}
                autoFocus={isWaitingForReply}
              />
              <Button
                type="submit"
                size="icon"
                disabled={inputDisabled || !input.trim()}
                aria-label={t("aiAgent.panel.sendLabel")}
                className="shrink-0 h-10 w-10 transition-opacity duration-150"
              >
                {isStreaming && !isWaitingForReply ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
            <p className="text-[10px] text-muted-foreground/60 text-center pb-2 leading-none">
              {t("aiAgent.panel.shiftEnterHint")}
            </p>
          </div>
        )}
      </div>

      {/* Reset confirmation dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("aiAgent.panel.resetConfirmTitle")}</DialogTitle>
            <DialogDescription>
              {t("aiAgent.panel.resetConfirmDesc")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 flex">
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              {t("aiAgent.panel.resetConfirmCancel")}
            </Button>
            <Button variant="destructive" onClick={handleReset}>
              <RotateCcw className="h-4 w-4 mr-2" />
              {t("aiAgent.panel.resetConfirmConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
